// Rebuilt from ../treasure-hunt/Editor/src/components/fly/FlyView.tsx (its spec
// 013, the fly-through view) for StoryMotion spec 025, the story studio's
// Fly-through map. A copy, not a shared package: the two may diverge.
//
// A Canvas 2D ground layer (grid, route ribbons with a flowing dash, choice
// labels, footprints, stems) under absolutely positioned DOM cards, both
// placed by one hand-written perspective projection (flyCamera). No three.js,
// no WebGL. Changes from the source: the cards are the studio's scene cards
// (`renderCard`), a click selects a scene and a click on empty ground keeps
// the selection, there is no toolbar, property panel or editing, only the 60
// nearest cards render (D-7), and viewing makes no request and writes nothing.
// From 025 v4: the camera follows every selection change it did not make by a
// single card click (D-10), is kept per story for the page session (D-11), the
// HUD draws above every card, and choice labels are trimmed and kept off the
// cards (D-14).
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flyPositions, LAYER_DEPTH, spineX, type RouteNode, type StoryRoute } from '../../utils/storyRoute';
import { recallFlyCamera, rememberFlyCamera } from '../../utils/storyView';
import {
  type Camera,
  type ChoiceLabel,
  type ScreenBox,
  type FlyCameraState,
  type FlyCardKind,
  NEAR,
  PAN_LIMIT,
  PITCH_MAX,
  PITCH_MIN,
  DISTANCE_MAX,
  DISTANCE_MIN,
  STEM_HEIGHT,
  cameraFor,
  cardBox,
  cardPlacement,
  clamp,
  clipToNear,
  flatCameraFor,
  fogAlpha,
  lerp,
  placeChoiceLabels,
  projectCamera,
  startCamera,
  toCamera,
  type Vec3,
} from '../../utils/flyCamera';
import styles from './FlyThrough.module.scss';

/** Drives the animation; tests pass a manual one to step frames. */
export interface FrameDriver {
  request(cb: (now: number) => void): number;
  cancel(id: number): void;
}

const rafDriver: FrameDriver = {
  request: (cb) =>
    typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame(cb)
      : (setTimeout(() => cb(performance.now()), 16) as unknown as number),
  cancel: (id) =>
    typeof window.cancelAnimationFrame === 'function' ? window.cancelAnimationFrame(id) : clearTimeout(id),
};

export interface FlyThroughProps {
  route: StoryRoute;
  selectedNodeId: string | null;
  /** A scene card was clicked (or N/P moved to it). */
  onSelect: (nodeId: string) => void;
  /** The card for a route node (the studio's `SceneCard`). */
  renderCard: (node: RouteNode) => React.ReactNode;
  frames?: FrameDriver;
  /** Overrides `prefers-reduced-motion`. */
  reducedMotion?: boolean;
  /** Keys the page-session camera memory (D-11); without it every entry starts over. */
  storyId?: number;
}

/** Length of the entering animation, seconds. */
export const TRANSITION_SECONDS = 1.1;
/** A second click on the same card within this many ms flies to it. */
export const DOUBLE_CLICK_MS = 350;
/** A press that moves less than this many px is a click. */
export const CLICK_SLOP_PX = 5;
/** Only the nearest cards render (D-7). */
export const MAX_CARDS = 60;

const COLORS = {
  skyTop: '#2a2a3a',
  skyLow: '#1d1d28',
  ground: '#15151d',
  grid: '#b87333',
  ribbon: '#b87333',
  choice: '#d4956a',
  scene: '#b87333',
  end: '#6b9a5a',
  selected: '#00bfff',
  label: '#e8e6e3',
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const isFormField = (el: EventTarget | null) => {
  if (!(el instanceof HTMLElement)) return false;
  return !!el.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]') || el.isContentEditable;
};

interface SimNode {
  key: string;
  kind: FlyCardKind;
  layer: number;
  x: number;
  z: number;
  tx: number;
  tz: number;
}

interface Sim {
  fly: FlyCameraState;
  target: FlyCameraState;
  /** Entering progress, 0 (top-down) to 1 (chase). */
  t: number;
  last: number | null;
  nodes: Map<string, SimNode>;
  route: StoryRoute;
  width: number;
  height: number;
  dpr: number;
  cam: Camera | null;
  selected: string | null;
  pendingFly: string | null;
  mini: { minX: number; maxX: number; minZ: number; sc: number; ox: number; oy: number } | null;
  dashT: number;
}

/** The stop the camera is at: the last stop layer ≤ s + 0.5, nearest sideways. */
export function currentStopIndex(
  route: StoryRoute,
  positions: Map<string, { x: number }>,
  fly: Pick<FlyCameraState, 's' | 'panX'>,
): number {
  const stops = route.stops;
  let layer = -1;
  for (const n of stops) if (n.layer <= fly.s + 0.5) layer = Math.max(layer, n.layer);
  if (layer < 0) return 0;
  const x = spineX(layer) + fly.panX;
  let best = -1;
  stops.forEach((n, i) => {
    if (n.layer !== layer) return;
    const nx = positions.get(n.id)?.x ?? 0;
    const bx = best >= 0 ? positions.get(stops[best].id)?.x ?? 0 : 0;
    if (best < 0 || Math.abs(nx - x) < Math.abs(bx - x) - 1e-9) best = i;
  });
  return Math.max(0, best);
}

const FlyThrough: React.FC<FlyThroughProps> = ({
  route, selectedNodeId, onSelect, renderCard, frames = rafDriver, reducedMotion, storyId,
}) => {
  const reduced = reducedMotion ?? prefersReducedMotion();
  const positions = useMemo(() => flyPositions(route), [route]);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const stageRef = useRef<HTMLDivElement | null>(null);
  const groundRef = useRef<HTMLCanvasElement | null>(null);
  const miniRef = useRef<HTMLCanvasElement | null>(null);
  const thumbRef = useRef<HTMLDivElement | null>(null);
  const fillRef = useRef<HTMLDivElement | null>(null);
  const cardEls = useRef(new Map<string, HTMLDivElement>());
  const ctxRef = useRef<CanvasRenderingContext2D | null | undefined>(undefined);
  const miniCtxRef = useRef<CanvasRenderingContext2D | null | undefined>(undefined);

  const sim = useRef<Sim>(null as unknown as Sim);
  /** The selection the camera last followed (or was recorded with). */
  const followed = useRef<string | null>(null);
  /** A single click on a card selected this: the camera stays where it is. */
  const selfSelected = useRef<string | null>(null);
  if (!sim.current) {
    // The first entry for a story starts over the start scene, straight along
    // the route, with the entering animation; a later one resumes the camera (D-9, D-11).
    const memory = storyId !== undefined ? recallFlyCamera(storyId, route.maxLayer) : null;
    followed.current = memory ? memory.selectedNodeId : selectedNodeId;
    sim.current = {
      fly: memory ? { ...memory.camera } : startCamera(),
      target: memory ? { ...memory.camera } : startCamera(),
      t: reduced || memory ? 1 : 0,
      last: null,
      nodes: new Map(),
      route,
      width: 0,
      height: 0,
      dpr: 1,
      cam: null,
      selected: selectedNodeId,
      pendingFly: null,
      mini: null,
      dashT: 0,
    };
  }

  const [stopIndex, setStopIndex] = useState(0);
  const hudRef = useRef({ stopIndex: 0 });
  const [helpOpen, setHelpOpen] = useState(false);

  // ── Drawing ────────────────────────────────────────────────────────────────

  const context = (ref: React.MutableRefObject<HTMLCanvasElement | null>, cache: React.MutableRefObject<CanvasRenderingContext2D | null | undefined>) => {
    if (cache.current !== undefined) return cache.current;
    let ctx: CanvasRenderingContext2D | null = null;
    try {
      ctx = ref.current?.getContext('2d') ?? null;
    } catch {
      ctx = null;
    }
    cache.current = ctx;
    return ctx;
  };

  const drawGround = useCallback((cam: Camera, cardBoxes: readonly ScreenBox[]) => {
    const s = sim.current;
    const ctx = context(groundRef, ctxRef);
    if (!ctx) return;
    const { width: W, height: H, dpr } = s;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalAlpha = 1;
    ctx.fillStyle = COLORS.ground;
    ctx.fillRect(0, 0, W, H);

    // Sky above the horizon, once tilted.
    const hy = cam.cy - cam.f * Math.tan(cam.pitch);
    if (hy > -40 && cam.pitch < Math.PI / 2 - 0.01) {
      const g = ctx.createLinearGradient(0, hy - H * 0.6, 0, hy + 60);
      g.addColorStop(0, COLORS.skyTop);
      g.addColorStop(0.85, COLORS.skyLow);
      g.addColorStop(1, COLORS.ground);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, Math.max(0, hy + 60));
    }

    const seg = (a: Vec3, b: Vec3) => clipToNear(a, b);

    // Grid, fading with depth.
    ctx.strokeStyle = COLORS.grid;
    ctx.lineWidth = 1;
    const z0 = -16;
    const z1 = s.route.maxLayer * LAYER_DEPTH + 40;
    const line = (ax: number, az: number, bx: number, bz: number) => {
      const N = 10;
      let pa = toCamera(cam, { x: ax, y: 0, z: az });
      for (let i = 1; i <= N; i++) {
        const pb = toCamera(cam, { x: lerp(ax, bx, i / N), y: 0, z: lerp(az, bz, i / N) });
        const c = seg(pa, pb);
        if (c) {
          const alpha = fogAlpha(cam, (c[0][2] + c[1][2]) / 2) * (0.16 - 0.06 * cam.e);
          if (alpha > 0.005) {
            const A = projectCamera(cam, c[0]);
            const B = projectCamera(cam, c[1]);
            ctx.globalAlpha = alpha;
            ctx.beginPath();
            ctx.moveTo(A.x, A.y);
            ctx.lineTo(B.x, B.y);
            ctx.stroke();
          }
        }
        pa = pb;
      }
    };
    for (let x = -26; x <= 26; x += 2) line(x, z0, x, z1);
    for (let z = z0; z <= z1; z += 2) line(-26, z, 26, z);
    ctx.globalAlpha = 1;

    // Edges: ribbons along a cubic on the ground, with a flowing dash and the choice label.
    const labels: Array<ChoiceLabel & { alpha: number }> = [];
    for (const ed of s.route.edges) {
      if (ed.loopBack) continue;
      const A = s.nodes.get(ed.from);
      const B = s.nodes.get(ed.to);
      if (!A || !B) continue;
      const pts: Vec3[] = [];
      const dz = B.z - A.z;
      for (let i = 0; i <= 16; i++) {
        const u = i / 16;
        const v = 1 - u;
        const x = v * v * v * A.x + 3 * v * v * u * A.x + 3 * v * u * u * B.x + u * u * u * B.x;
        const z =
          v * v * v * A.z + 3 * v * v * u * (A.z + dz * 0.5) + 3 * v * u * u * (B.z - dz * 0.5) + u * u * u * B.z;
        pts.push(toCamera(cam, { x, y: 0.01, z }));
      }
      const hot = !!s.selected && (ed.from === s.selected || ed.to === s.selected);
      ctx.strokeStyle = ed.choiceId ? COLORS.choice : COLORS.ribbon;
      ctx.lineCap = 'round';
      const segs: { x: number; y: number }[][] = [];
      for (let i = 1; i < pts.length; i++) {
        const c = seg(pts[i - 1], pts[i]);
        if (!c) continue;
        const zm = (c[0][2] + c[1][2]) / 2;
        const P = projectCamera(cam, c[0]);
        const Q = projectCamera(cam, c[1]);
        segs.push([P, Q]);
        ctx.globalAlpha = fogAlpha(cam, zm) * (hot ? 0.95 : 0.55);
        ctx.lineWidth = clamp((cam.f * (hot ? 0.2 : 0.13)) / zm, 1.5, 22);
        ctx.beginPath();
        ctx.moveTo(P.x, P.y);
        ctx.lineTo(Q.x, Q.y);
        ctx.stroke();
      }
      if (segs.length) {
        ctx.globalAlpha = 0.9 * (1 - cam.e * 0.2);
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([3, 9]);
        ctx.lineDashOffset = reduced ? 0 : -s.dashT * 0.03;
        ctx.beginPath();
        ctx.moveTo(segs[0][0].x, segs[0][0].y);
        for (const [, Q] of segs) ctx.lineTo(Q.x, Q.y);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      // The choice's text at the ribbon's middle, placed below (D-14).
      if (ed.label) {
        const mid = pts[8];
        if (mid[2] > NEAR) {
          const M = projectCamera(cam, mid);
          labels.push({
            text: ed.label,
            x: M.x,
            y: M.y,
            depth: mid[2],
            fontSize: Math.round(clamp((cam.f * 0.32) / mid[2], 10, 18)),
            alpha: fogAlpha(cam, mid[2]),
          });
        }
      }
    }
    // Choice labels: trimmed, nearest first, none over a card or another label (D-14).
    const font = (size: number) => `600 ${size}px Rajdhani, sans-serif`;
    const measure = (text: string, size: number) => {
      ctx.font = font(size);
      const m = ctx.measureText?.(text);
      return m && Number.isFinite(m.width) ? m.width : text.length * size * 0.55;
    };
    ctx.fillStyle = COLORS.label;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const l of placeChoiceLabels(labels, cardBoxes, measure) as Array<ChoiceLabel & { alpha: number }>) {
      ctx.globalAlpha = l.alpha;
      ctx.font = font(l.fontSize);
      ctx.fillText(l.text, l.x, l.y);
    }
    ctx.globalAlpha = 1;

    // Footprints and stems.
    for (const n of s.nodes.values()) {
      const r = n.kind === 'scene' ? 0.55 : 0.38;
      const ring: { x: number; y: number }[] = [];
      let ok = true;
      for (let i = 0; i < 20; i++) {
        const a = (i / 20) * Math.PI * 2;
        const v = toCamera(cam, { x: n.x + Math.cos(a) * r, y: 0.02, z: n.z + Math.sin(a) * r });
        if (v[2] < NEAR) {
          ok = false;
          break;
        }
        ring.push(projectCamera(cam, v));
      }
      if (!ok) continue;
      const g = toCamera(cam, { x: n.x, y: 0, z: n.z });
      const col = n.kind === 'scene' ? COLORS.scene : COLORS.end;
      ctx.globalAlpha = fogAlpha(cam, g[2]) * 0.9;
      ctx.fillStyle = col;
      ctx.beginPath();
      ring.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.closePath();
      ctx.fill();
      if (n.key === s.selected) {
        ctx.strokeStyle = COLORS.selected;
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        for (let i = 0; i <= 24; i++) {
          const a = (i / 24) * Math.PI * 2;
          const v = toCamera(cam, { x: n.x + Math.cos(a) * r * 1.6, y: 0.02, z: n.z + Math.sin(a) * r * 1.6 });
          if (v[2] < NEAR) continue;
          const p = projectCamera(cam, v);
          if (i) ctx.lineTo(p.x, p.y);
          else ctx.moveTo(p.x, p.y);
        }
        ctx.stroke();
      }
      if (cam.e > 0.02) {
        const top = toCamera(cam, { x: n.x, y: STEM_HEIGHT[n.kind] * cam.e, z: n.z });
        if (top[2] > NEAR) {
          const P = projectCamera(cam, g);
          const Q = projectCamera(cam, top);
          ctx.strokeStyle = col;
          ctx.lineWidth = clamp((cam.f * 0.035) / g[2], 1, 4);
          ctx.beginPath();
          ctx.moveTo(P.x, P.y);
          ctx.lineTo(Q.x, Q.y);
          ctx.stroke();
        }
      }
    }
    ctx.globalAlpha = 1;
  }, [reduced]);

  /** The overview map (start at the top) with the camera's view wedge. */
  const drawMini = useCallback((cam: Camera) => {
    const s = sim.current;
    const canvas = miniRef.current;
    if (!canvas) return;
    const w = canvas.width;
    const h = canvas.height;
    const xs = [...s.nodes.values()].map((n) => n.x);
    const minX = (xs.length ? Math.min(...xs) : -4) - 3;
    const maxX = (xs.length ? Math.max(...xs) : 4) + 3;
    const minZ = -3;
    const maxZ = s.route.maxLayer * LAYER_DEPTH + 3;
    const sc = Math.min(w / (maxX - minX), h / (maxZ - minZ));
    const ox = (w - (maxX - minX) * sc) / 2;
    const oy = (h - (maxZ - minZ) * sc) / 2;
    s.mini = { minX, maxX, minZ, sc, ox, oy };
    const ctx = context(miniRef, miniCtxRef);
    if (!ctx) return;
    const M = (x: number, z: number) => [ox + (x - minX) * sc, oy + (z - minZ) * sc];
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#1a1a24';
    ctx.fillRect(0, 0, w, h);
    ctx.lineWidth = 2;
    for (const ed of s.route.edges) {
      if (ed.loopBack) continue;
      const A = s.nodes.get(ed.from);
      const B = s.nodes.get(ed.to);
      if (!A || !B) continue;
      const p = M(A.x, A.z);
      const q = M(B.x, B.z);
      ctx.strokeStyle = COLORS.ribbon;
      ctx.globalAlpha = 0.6;
      ctx.beginPath();
      ctx.moveTo(p[0], p[1]);
      ctx.lineTo(q[0], q[1]);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    for (const n of s.nodes.values()) {
      const p = M(n.x, n.z);
      ctx.fillStyle = n.kind === 'scene' ? COLORS.scene : COLORS.end;
      ctx.beginPath();
      ctx.arc(p[0], p[1], n.kind === 'scene' ? 6 : 4, 0, Math.PI * 2);
      ctx.fill();
      if (n.key === s.selected) {
        ctx.strokeStyle = COLORS.selected;
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    }
    // The camera's view wedge.
    const C = M(cam.eye.x, cam.eye.z);
    const half = (cam.fov * (s.width / Math.max(1, s.height))) / 2;
    const dir = (a: number) => M(cam.eye.x + Math.sin(cam.heading + a) * 30, cam.eye.z + Math.cos(cam.heading + a) * 30);
    const l = dir(-half);
    const r = dir(half);
    ctx.fillStyle = COLORS.selected;
    ctx.globalAlpha = 0.18;
    ctx.beginPath();
    ctx.moveTo(C[0], C[1]);
    ctx.lineTo(l[0], l[1]);
    ctx.lineTo(r[0], r[1]);
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 1;
  }, []);

  /** Places cards, draws, and syncs the HUD for the current state. */
  const render = useCallback(() => {
    const s = sim.current;
    const stage = stageRef.current;
    if (!stage) return;
    if (!s.width || !s.height) {
      s.width = stage.clientWidth || window.innerWidth || 1024;
      s.height = stage.clientHeight || window.innerHeight || 768;
    }
    const cam = cameraFor(s.fly, flatCameraFor(s.route.maxLayer), s.t, { width: s.width, height: s.height });
    s.cam = cam;

    // Only the nearest MAX_CARDS visible cards render (D-7).
    const placed: Array<{ el: HTMLDivElement; key: string; kind: FlyCardKind; p: ReturnType<typeof cardPlacement> }> = [];
    for (const n of s.nodes.values()) {
      const el = cardEls.current.get(n.key);
      if (!el) continue;
      const p = cardPlacement(cam, n);
      if (!p.visible) {
        el.style.display = 'none';
        el.dataset.visible = 'false';
        continue;
      }
      placed.push({ el, key: n.key, kind: n.kind, p });
    }
    placed.sort((a, b) => a.p.depth - b.p.depth);

    // The ground goes under the cards; its labels keep off the ones shown.
    drawGround(cam, placed.slice(0, MAX_CARDS).map(({ p, kind }) => cardBox(p, kind)));

    placed.forEach(({ el, key, p }, i) => {
      if (i >= MAX_CARDS) {
        el.style.display = 'none';
        el.dataset.visible = 'false';
        return;
      }
      el.style.display = '';
      el.dataset.visible = 'true';
      el.style.opacity = p.opacity.toFixed(3);
      el.style.zIndex = String(key === s.selected ? 99999 : Math.max(1, Math.round(90000 - p.depth * 100)));
      el.style.transform = `translate(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px) scale(${p.scale.toFixed(4)}) translate(-50%, ${p.anchorY.toFixed(1)}%)`;
    });

    drawMini(cam);

    // HUD
    const idx = currentStopIndex(s.route, positions, s.fly);
    if (idx !== hudRef.current.stopIndex) {
      hudRef.current.stopIndex = idx;
      setStopIndex(idx);
    }
    const pct = `${((s.fly.s / s.route.maxLayer) * 100).toFixed(2)}%`;
    if (thumbRef.current) thumbRef.current.style.left = pct;
    if (fillRef.current) fillRef.current.style.width = pct;

    const d = stage.dataset;
    d.s = s.fly.s.toFixed(4);
    d.sTarget = s.target.s.toFixed(4);
    d.pan = s.fly.panX.toFixed(4);
    d.panTarget = s.target.panX.toFixed(4);
    d.yaw = s.fly.yaw.toFixed(4);
    d.yawTarget = s.target.yaw.toFixed(4);
    d.pitch = s.fly.pitch.toFixed(4);
    d.pitchTarget = s.target.pitch.toFixed(4);
    d.distance = s.fly.distance.toFixed(4);
    d.distanceTarget = s.target.distance.toFixed(4);
    d.transition = s.t.toFixed(4);
  }, [drawGround, drawMini, positions]);

  /** One animation frame at time `now` (ms). */
  const step = useCallback(
    (now: number) => {
      const s = sim.current;
      const dt = s.last == null ? 0 : Math.min(0.05, Math.max(0, (now - s.last) / 1000));
      s.last = now;
      s.dashT = now;
      const k = reduced ? 1 : 1 - Math.exp(-dt * 9);
      const f = s.fly;
      const g = s.target;
      f.s += (g.s - f.s) * k;
      f.panX += (g.panX - f.panX) * k;
      f.yaw += (g.yaw - f.yaw) * k;
      f.pitch += (g.pitch - f.pitch) * k;
      f.distance += (g.distance - f.distance) * k;
      s.t = reduced ? 1 : Math.min(1, s.t + dt / TRANSITION_SECONDS);
      const kn = reduced ? 1 : 1 - Math.exp(-dt * 10);
      for (const n of s.nodes.values()) {
        n.x += (n.tx - n.x) * kn;
        n.z += (n.tz - n.z) * kn;
      }
      render();
    },
    [reduced, render]
  );

  // ── Route sync ─────────────────────────────────────────────────────────────

  const flyTo = useCallback(
    (key: string) => {
      const s = sim.current;
      const n = s.nodes.get(key);
      if (!n) {
        s.pendingFly = key;
        return;
      }
      s.pendingFly = null;
      s.target.s = clamp(n.layer, 0, s.route.maxLayer);
      s.target.panX = clamp(n.tx - spineX(n.layer), -PAN_LIMIT, PAN_LIMIT);
      render();
    },
    [render]
  );

  useLayoutEffect(() => {
    const s = sim.current;
    s.route = route;
    const seen = new Set<string>();
    for (const rn of route.nodes) {
      const pos = positions.get(rn.id)!;
      seen.add(rn.id);
      const existing = s.nodes.get(rn.id);
      if (existing) {
        existing.layer = rn.layer;
        existing.tx = pos.x;
        existing.tz = pos.z;
        if (reduced) {
          existing.x = pos.x;
          existing.z = pos.z;
        }
      } else {
        s.nodes.set(rn.id, { key: rn.id, kind: rn.kind, layer: rn.layer, x: pos.x, z: pos.z, tx: pos.x, tz: pos.z });
      }
    }
    for (const key of [...s.nodes.keys()]) if (!seen.has(key)) s.nodes.delete(key);
    s.target.s = clamp(s.target.s, 0, route.maxLayer);
    s.fly.s = clamp(s.fly.s, 0, route.maxLayer);
    if (s.pendingFly && s.nodes.has(s.pendingFly)) flyTo(s.pendingFly);
    render();
  }, [route, positions, reduced, flyTo, render]);

  // The camera follows every selection change but a single click on a card
  // here (D-10): it flies as "Recenter" does (in one frame with reduced motion).
  useLayoutEffect(() => {
    sim.current.selected = selectedNodeId;
    if (selectedNodeId !== followed.current) {
      followed.current = selectedNodeId;
      const own = selfSelected.current === selectedNodeId;
      selfSelected.current = null;
      if (selectedNodeId && !own) flyTo(selectedNodeId);
    }
    render();
  }, [selectedNodeId, render, flyTo]);

  // Leaving records the camera for the story's next entry this page session (D-11).
  useEffect(() => () => {
    if (storyId === undefined) return;
    const s = sim.current;
    rememberFlyCamera(storyId, s.target, s.selected);
  }, [storyId]);

  // ── Frame loop and resize ────────────────────────────────────────────────

  useEffect(() => {
    let id = 0;
    let alive = true;
    const loop = (now: number) => {
      if (!alive) return;
      step(now);
      id = frames.request(loop);
    };
    id = frames.request(loop);
    return () => {
      alive = false;
      frames.cancel(id);
    };
  }, [frames, step]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const measure = () => {
      const s = sim.current;
      s.width = stage.clientWidth || window.innerWidth || 1024;
      s.height = stage.clientHeight || window.innerHeight || 768;
      s.dpr = Math.min(2, window.devicePixelRatio || 1);
      const ground = groundRef.current;
      if (ground) {
        ground.width = Math.round(s.width * s.dpr);
        ground.height = Math.round(s.height * s.dpr);
      }
      render();
    };
    measure();
    if (typeof ResizeObserver !== 'function') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(stage);
    return () => ro.disconnect();
  }, [render]);

  // ── Navigation ───────────────────────────────────────────────────────────

  const zoom = useCallback(
    (factor: number) => {
      const g = sim.current.target;
      g.distance = clamp(g.distance * factor, DISTANCE_MIN, DISTANCE_MAX);
      render();
    },
    [render]
  );

  const tilt = useCallback(() => {
    const g = sim.current.target;
    g.pitch = g.pitch > 0.7 ? 0.42 : 1.05;
    render();
  }, [render]);

  const jumpStop = useCallback(
    (dir: 1 | -1) => {
      const s = sim.current;
      const cur = s.target.s;
      const list =
        dir > 0
          ? s.route.stops.filter((n) => n.layer > cur + 0.3)
          : s.route.stops.filter((n) => n.layer < cur - 0.3).reverse();
      const next = list[0];
      if (!next) return;
      flyTo(next.id);
      onSelectRef.current(next.id);
    },
    [flyTo]
  );

  // Wheel, pointer and context menu on the stage.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const s = sim.current;

    const onWheel = (ev: WheelEvent) => {
      // The page never scrolls from a wheel on the stage.
      ev.preventDefault();
      const scale = ev.deltaMode === 1 ? 16 : 1;
      const dy = ev.deltaY * scale;
      const g = s.target;
      if (ev.ctrlKey || ev.metaKey) {
        g.distance = clamp(g.distance * Math.exp(dy * 0.01), DISTANCE_MIN, DISTANCE_MAX);
      } else if (ev.altKey) {
        g.pitch = clamp(g.pitch + dy * 0.002, PITCH_MIN, PITCH_MAX);
      } else if (Math.abs(ev.deltaX) > Math.abs(ev.deltaY)) {
        // A mostly horizontal scroll never moves the camera off the route.
        return;
      } else {
        g.s = clamp(g.s - dy * 0.0045, 0, s.route.maxLayer);
      }
      render();
    };

    const pointers = new Map<number, { x: number; y: number }>();
    let drag: { x: number; y: number; moved: number; orbit: boolean; node: string | null } | null = null;
    let pinch: { d: number } | null = null;
    let lastClick = { key: null as string | null, at: 0 };

    const onDown = (ev: PointerEvent) => {
      pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      try {
        stage.setPointerCapture?.(ev.pointerId);
      } catch {
        // Not capturable (synthetic pointer).
      }
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinch = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1 };
        drag = null;
        return;
      }
      const card = (ev.target as HTMLElement | null)?.closest?.('[data-node-key]') as HTMLElement | null;
      drag = {
        x: ev.clientX,
        y: ev.clientY,
        moved: 0,
        orbit: ev.shiftKey || ev.button === 2,
        node: card?.dataset.nodeKey ?? null,
      };
      stage.classList.add(styles.dragging);
    };

    const onMove = (ev: PointerEvent) => {
      if (!pointers.has(ev.pointerId)) return;
      pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      if (pinch && pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
        zoom(pinch.d / d);
        pinch.d = d;
        return;
      }
      if (!drag) return;
      const dx = ev.clientX - drag.x;
      const dy = ev.clientY - drag.y;
      drag.x = ev.clientX;
      drag.y = ev.clientY;
      drag.moved += Math.abs(dx) + Math.abs(dy);
      if (drag.moved < CLICK_SLOP_PX) return;
      const cam = s.cam;
      const wpp = cam ? (2 * cam.distance * Math.tan(cam.fov / 2)) / Math.max(1, s.height) : 0.02;
      const g = s.target;
      if (drag.orbit) {
        g.yaw -= dx * 0.005;
        g.pitch = clamp(g.pitch + dy * 0.004, PITCH_MIN, PITCH_MAX);
      } else {
        g.panX = clamp(g.panX - dx * wpp, -PAN_LIMIT, PAN_LIMIT);
        s.fly.panX = g.panX;
        g.s = clamp(g.s + (dy * wpp * 1.6) / LAYER_DEPTH, 0, s.route.maxLayer);
        s.fly.s = g.s;
      }
      render();
    };

    const onUp = (ev: PointerEvent) => {
      pointers.delete(ev.pointerId);
      if (pointers.size < 2) pinch = null;
      if (drag && drag.moved < CLICK_SLOP_PX && ev.type === 'pointerup') {
        const now = performance.now();
        const node = drag.node ? s.nodes.get(drag.node) : undefined;
        if (drag.node && node) {
          if (lastClick.key === drag.node && now - lastClick.at < DOUBLE_CLICK_MS) flyTo(drag.node);
          // A click selects a scene, and the camera stays; the end card has no scene to show.
          if (node.kind === 'scene') {
            selfSelected.current = drag.node;
            onSelectRef.current(drag.node);
          }
          lastClick = { key: drag.node, at: now };
        } else {
          // Empty ground keeps the selection: the studio always previews a scene.
          lastClick = { key: null, at: 0 };
        }
      }
      drag = null;
      stage.classList.remove(styles.dragging);
    };

    const onContext = (ev: MouseEvent) => ev.preventDefault();

    stage.addEventListener('wheel', onWheel, { passive: false });
    stage.addEventListener('pointerdown', onDown);
    stage.addEventListener('pointermove', onMove);
    stage.addEventListener('pointerup', onUp);
    stage.addEventListener('pointercancel', onUp);
    stage.addEventListener('contextmenu', onContext);
    return () => {
      stage.removeEventListener('wheel', onWheel);
      stage.removeEventListener('pointerdown', onDown);
      stage.removeEventListener('pointermove', onMove);
      stage.removeEventListener('pointerup', onUp);
      stage.removeEventListener('pointercancel', onUp);
      stage.removeEventListener('contextmenu', onContext);
    };
  }, [render, zoom, flyTo]);

  // Keyboard (not while typing, not under a modal).
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.defaultPrevented || ev.ctrlKey || ev.metaKey || ev.altKey) return;
      if (isFormField(ev.target)) return;
      if (document.querySelector('.modal.show, [aria-modal="true"]')) return;
      const s = sim.current;
      const g = s.target;
      const k = ev.key.length === 1 ? ev.key.toLowerCase() : ev.key;
      switch (k) {
        case 'ArrowUp':
        case 'w':
          g.s = clamp(g.s + 0.5, 0, s.route.maxLayer);
          break;
        case 'ArrowDown':
        case 's':
          g.s = clamp(g.s - 0.5, 0, s.route.maxLayer);
          break;
        case 'ArrowLeft':
        case 'a':
          g.panX = clamp(g.panX - 1, -PAN_LIMIT, PAN_LIMIT);
          break;
        case 'ArrowRight':
        case 'd':
          g.panX = clamp(g.panX + 1, -PAN_LIMIT, PAN_LIMIT);
          break;
        case 'q':
          g.yaw -= 0.15;
          break;
        case 'e':
          g.yaw += 0.15;
          break;
        case '+':
        case '=':
          zoom(1 / 1.25);
          break;
        case '-':
        case '_':
          zoom(1.25);
          break;
        case 't':
          tilt();
          break;
        case 'n':
          jumpStop(1);
          break;
        case 'p':
          jumpStop(-1);
          break;
        default:
          return;
      }
      ev.preventDefault();
      render();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [render, zoom, tilt, jumpStop]);

  // ── HUD handlers ─────────────────────────────────────────────────────────

  const onTrackDown = (ev: React.PointerEvent<HTMLDivElement>) => {
    if ((ev.target as HTMLElement).closest(`.${styles.tick}`)) return;
    const track = ev.currentTarget;
    const scrub = (clientX: number) => {
      const r = track.getBoundingClientRect();
      const s = sim.current;
      s.target.s = clamp((clientX - r.left) / Math.max(1, r.width), 0, 1) * s.route.maxLayer;
      render();
    };
    scrub(ev.clientX);
    const move = (e: PointerEvent) => scrub(e.clientX);
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const onMiniClick = (ev: React.MouseEvent<HTMLCanvasElement>) => {
    const s = sim.current;
    const m = s.mini;
    const canvas = miniRef.current;
    if (!m || !canvas) return;
    const r = canvas.getBoundingClientRect();
    const mx = ((ev.clientX - r.left) * canvas.width) / Math.max(1, r.width);
    const my = ((ev.clientY - r.top) * canvas.height) / Math.max(1, r.height);
    const x = (mx - m.ox) / m.sc + m.minX;
    const z = (my - m.oy) / m.sc + m.minZ;
    s.target.s = clamp(z / LAYER_DEPTH, 0, s.route.maxLayer);
    s.target.panX = clamp(x - spineX(s.target.s), -PAN_LIMIT, PAN_LIMIT);
    render();
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const stops = route.stops;
  const current = stops[Math.min(stopIndex, stops.length - 1)];

  const setCardRef = (key: string) => (el: HTMLDivElement | null) => {
    if (el) cardEls.current.set(key, el);
    else cardEls.current.delete(key);
  };

  return (
    <div className={styles.view} data-testid="fly-view">
      <div ref={stageRef} className={styles.stage} data-testid="fly-stage" aria-label="Fly-through of the story">
        <canvas ref={groundRef} className={styles.ground} />
        <div className={styles.cards}>
          {route.nodes.map((n) => (
            <div
              key={n.id}
              ref={setCardRef(n.id)}
              className={styles.card}
              data-node-key={n.id}
              data-layer={n.layer}
              data-testid={`fly-card-${n.id}`}
              style={{ display: 'none' }}
            >
              {renderCard(n)}
            </div>
          ))}
        </div>
        {stops.length === 0 && <div className={styles.empty}>Your story will appear here.</div>}
      </div>

      <div className={styles.hudLayer}>
        {helpOpen ? (
          <div className={`${styles.hud} ${styles.help}`} data-testid="fly-help">
            <div className={styles.helpHead}>
              <span>Fly-through controls</span>
              <button type="button" className={styles.x} aria-label="Hide controls" onClick={() => setHelpOpen(false)}>✕</button>
            </div>
            <dl>
              <dt>Scroll</dt><dd>Travel along the path</dd>
              <dt>Ctrl+scroll</dt><dd>Zoom (pinch on a trackpad)</dd>
              <dt>Alt+scroll</dt><dd>Tilt</dd>
              <dt>Drag</dt><dd>Slide sideways, travel</dd>
              <dt>Shift+drag</dt><dd>Turn and tilt</dd>
              <dt>Click</dt><dd>Show a scene</dd>
              <dt>Double-click</dt><dd>Fly to a scene</dd>
              <dt>W A S D</dt><dd>Travel, slide</dd>
              <dt>Q / E</dt><dd>Turn</dd>
              <dt>N / P</dt><dd>Next / previous scene</dd>
              <dt>T</dt><dd>Steep / shallow</dd>
              <dt>V</dt><dd>Back to the path</dd>
            </dl>
          </div>
        ) : (
          <button type="button" className={`${styles.hud} ${styles.helpBtn}`} onClick={() => setHelpOpen(true)}>
            Controls
          </button>
        )}

        {selectedNodeId && route.nodes.some((n) => n.id === selectedNodeId) && (
          <button
            type="button"
            className={`${styles.hud} ${styles.here}`}
            onClick={() => flyTo(selectedNodeId)}
            title="Bring the scene you are looking at back to the middle"
            data-testid="fly-recenter"
          >
            Recenter
          </button>
        )}

        <div className={`${styles.hud} ${styles.mini}`} title="Overview: click to fly there">
          <canvas ref={miniRef} width={300} height={240} onClick={onMiniClick} data-testid="fly-mini" />
        </div>

        {stops.length > 0 && current && (
          <div className={`${styles.hud} ${styles.stopbar}`} data-testid="fly-stopbar">
            <div className={styles.stopbarLabel}>
              <b data-testid="fly-stop-name">{current.name}</b>
              <span data-testid="fly-stop-number">Scene {current.stop} of {stops.length}</span>
            </div>
            <button type="button" className={styles.btn} title="Previous scene (P)" aria-label="Previous scene" onClick={() => jumpStop(-1)}>‹</button>
            <div className={styles.track} onPointerDown={onTrackDown} data-testid="fly-track">
              <div className={styles.trackFill} ref={fillRef} />
              {stops.map((n) => (
                <button
                  type="button"
                  key={n.id}
                  className={`${styles.tick}${n.layer < current.layer ? ` ${styles.past}` : ''}`}
                  style={{ left: `${((n.layer / route.maxLayer) * 100).toFixed(2)}%` }}
                  title={`Scene ${n.stop}: ${n.name}`}
                  aria-label={`Scene ${n.stop}: ${n.name}`}
                  data-testid={`fly-tick-${n.id}`}
                  onClick={() => {
                    flyTo(n.id);
                    onSelectRef.current(n.id);
                  }}
                />
              ))}
              <div className={styles.thumb} ref={thumbRef} />
            </div>
            <button type="button" className={styles.btn} title="Next scene (N)" aria-label="Next scene" onClick={() => jumpStop(1)}>›</button>
          </div>
        )}

        <div className={`${styles.hud} ${styles.nav}`}>
          <button type="button" className={styles.btn} title="Zoom in (+)" aria-label="Zoom in" onClick={() => zoom(1 / 1.25)}>+</button>
          <button type="button" className={styles.btn} title="Zoom out (−)" aria-label="Zoom out" onClick={() => zoom(1.25)}>−</button>
          <button type="button" className={styles.btn} title="Tilt: steep / shallow (T)" aria-label="Tilt" onClick={tilt}>⟂</button>
        </div>
      </div>
    </div>
  );
};

export default FlyThrough;
